/**
 * useAutoSave
 *
 * Debounced auto-save hook. Compares data via JSON.stringify to detect
 * changes and fires onSave after a configurable debounce delay.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseAutoSaveOptions {
  data: unknown;
  onSave: () => Promise<void>;
  delay?: number;
  enabled?: boolean;
}

export interface UseAutoSaveResult {
  isSaving: boolean;
  lastSaved: Date | null;
  isDirty: boolean;
}

export function useAutoSave({
  data,
  onSave,
  delay = 3000,
  enabled = true,
}: UseAutoSaveOptions): UseAutoSaveResult {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Track the last-saved snapshot as a JSON string
  const savedSnapshot = useRef<string>(JSON.stringify(data));
  // Skip the first render (don't auto-save on mount)
  const isFirstMount = useRef(true);
  // Timer ref for debounce cleanup
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable ref to the current onSave callback
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  const serialized = JSON.stringify(data);

  const executeSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSaveRef.current();
      savedSnapshot.current = JSON.stringify(data);
      setLastSaved(new Date());
      setIsDirty(false);
    } catch (err) {
      console.error('[useAutoSave] save failed', err);
    } finally {
      setIsSaving(false);
    }
    // data intentionally omitted — we capture at call time via closure snapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip first mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (!enabled) return;

    const hasChanged = serialized !== savedSnapshot.current;
    setIsDirty(hasChanged);

    if (!hasChanged) return;

    // Clear any pending timer
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    // Schedule save after delay
    timerRef.current = setTimeout(() => {
      executeSave();
    }, delay);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, enabled, delay]);

  return { isSaving, lastSaved, isDirty };
}
